import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchSistemaMetrics } from '../api/sistemaBridge';
import { applyHistoricalFinancial2026, calculateDynamicFinancialTotals } from '../utils/historicalFinancial2026';
import { createFinancialMatrix, ensureMonthlyValues, FINANCIAL_INCOME_KEY, movementAmountUsd } from '../utils/financialSummary';

const expenseCategory = movement => {
  const value = `${movement.categoria || ''} ${movement.subcategoria || ''}`.toLowerCase();
  if (value.includes('cuenta') || value.includes('inventario')) return 'Cuentas';
  if (value.includes('emple') || value.includes('personal') || value.includes('nomina') || value.includes('nómina')) return 'Empleados';
  if (value.includes('licencia') || value.includes('software')) return 'Licencias';
  if (value.includes('public')) return 'Publicidad';
  if (value.includes('retiro') && value.includes('fondo')) return 'Retiro Fondo de Reserva';
  if (value.includes('membres')) return 'Membresías';
  return 'Otros';
};

export const useDashboardData = () => {
  const [data, setData] = useState({ totalUsers: 0, ingresosMes: 0, gastosMes: 0, utilidadNeta: 0, tableData: null });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const fechaInicioAño = Timestamp.fromDate(new Date(currentYear, 0, 1));
      const movimientosQuery = query(
        collection(db, 'finanzas_movimientos'),
        where('fecha', '>=', fechaInicioAño)
      );
      const [usersSnap, movimientosSnap, sistemaMetrics] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(movimientosQuery),
        fetchSistemaMetrics(currentYear, currentMonth + 1)
      ]);
      const tableData = createFinancialMatrix();
      movimientosSnap.forEach(snapshot => {
        const movement = snapshot.data(), dateValue = movement.created_at || movement.fecha || movement.date;
        const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
        const month = date.getMonth(), year = date.getFullYear();
        if (year !== currentYear || month < 0 || month > 11) return;
        const amount = movementAmountUsd(movement, sistemaMetrics.tasa_usd);
        if (movement.tipo === 'ingreso') ensureMonthlyValues(tableData.ingresos, FINANCIAL_INCOME_KEY)[month] += amount;
        if (movement.tipo === 'gasto') ensureMonthlyValues(tableData.gastos, expenseCategory(movement))[month] += amount;
        if (movement.tipo === 'retiro') { ensureMonthlyValues(tableData.retiros, movement.socio || 'Otros')[month] += amount; tableData.totalRetiros[month] += amount; }
      });
      const sistemaIngresos = sistemaMetrics.series_anuales?.ingresos || [], sistemaGastos = sistemaMetrics.series_anuales?.gastos || [];
      for (let month = 0; month < 12; month += 1) { ensureMonthlyValues(tableData.ingresos, FINANCIAL_INCOME_KEY)[month] += Number(sistemaIngresos[month] || 0); ensureMonthlyValues(tableData.gastos, 'Cuentas')[month] += Number(sistemaGastos[month] || 0); }
      applyHistoricalFinancial2026(tableData, currentYear);
      calculateDynamicFinancialTotals(tableData);
      setData({ totalUsers: usersSnap.size, ingresosMes: Number(tableData.totalIngresos[currentMonth] || 0), gastosMes: Number(tableData.totalGastos[currentMonth] || 0), utilidadNeta: Number(tableData.utilidadNeta[currentMonth] || 0), tableData });
    } catch (error) {
      console.error('Error obteniendo datos del Dashboard:', error);
      Swal.fire('Error de conexión', 'No se pudieron obtener los datos financieros', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  return { data, loading, refetch: fetchData };
};
